/**
 * Simulation media preloader hook.
 *
 * Preloads all media files (section titles, contexts, introductions) for all
 * modules in a simulation before starting. Ensures all media is ready to prevent
 * loading errors during module execution.
 *
 * @module useSimulationMediaPreloader
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  fetchReadingModuleData,
  fetchListeningModuleData,
  fetchModuleIntroduction,
  fetchSpeakingModuleData,
  findSimulationSettings
} from '@/lib/examApi';
import { STALE_TIME_FIVE_MINUTES } from '@/lib/constants';
import { logger } from '@/lib/logger';
import type {
  CEFRLevel,
  ExamType,
  ExamModule,
  SimulationSettings,
  SimulationData,
} from '@/types/exam-simulation';

interface MediaFile {
  url: string;
  module: ExamModule;
  section?: number;
  type: 'section-title' | 'context' | 'introduction';
  contextId?: string;
}

interface PreloadProgress {
  totalFiles: number;
  loadedFiles: number;
  failedFiles: number;
  currentModule: ExamModule | null;
  errors: Array<{ url: string; error: string }>;
}

interface UseSimulationMediaPreloaderOptions {
  level: CEFRLevel;
  examType: ExamType;
  modules: ExamModule[];
  settings: SimulationSettings | null;
  userId?: string | null;
  enabled?: boolean;
}

interface UseSimulationMediaPreloaderReturn {
  isLoading: boolean;
  isComplete: boolean;
  progress: PreloadProgress;
  error: Error | null;
  startPreloading: () => Promise<void>;
}

/**
 * Helper to extract section title media URLs
 */
function extractSectionTitles(sections: any[], module: ExamModule): MediaFile[] {
  const files: MediaFile[] = [];
  for (const section of sections) {
    if (section.material_url && section.material_type === 'audio') {
      files.push({
        url: section.material_url,
        module,
        section: section.section,
        type: 'section-title',
      });
    }
  }
  return files;
}

/**
 * Helper to extract speaking-specific media URLs
 */
function extractSpeakingMaterials(questions: any[], module: ExamModule, section: number, contextId?: string): MediaFile[] {
  const files: MediaFile[] = [];
  if (module !== 'speaking') return files;

  for (const question of questions) {
    if (question.materialUrl) {
      files.push({
        url: question.materialUrl,
        module,
        section: section,
        type: 'context',
        contextId,
      });
    }
  }
  return files;
}

/**
 * Helper to extract context media URLs
 */
function extractContextMedia(sections: any[], module: ExamModule): MediaFile[] {
  const files: MediaFile[] = [];
  for (const section of sections) {
    if (!section.questionGroups) continue;

    for (const group of section.questionGroups) {
      if (group.context?.mediaUrl && group.context.type === 'audio') {
        files.push({
          url: group.context.mediaUrl,
          module,
          section: section.section,
          type: 'context',
          contextId: group.contextId,
        });
      }

      files.push(...extractSpeakingMaterials(group.questions || [], module, section.section, group.contextId));
    }
  }
  return files;
}

/**
 * Extract all media URLs from simulation data
 */
function extractMediaUrls(
  data: SimulationData,
  module: ExamModule,
): MediaFile[] {
  return [
    ...extractSectionTitles(data.sections, module),
    ...extractContextMedia(data.sections, module),
  ];
}

/**
 * Preload a single media file
 */
async function preloadMediaFile(file: MediaFile): Promise<boolean> {
  try {
    logger.info('Preloading media file', {
      url: file.url,
      module: file.module,
      section: file.section,
      type: file.type,
      contextId: file.contextId,
    });

    // For relative URLs, ensure they're properly formatted
    // The backend serves media at /media/... so relative URLs should work
    const mediaUrl = file.url.startsWith('/') ? file.url : `/${file.url}`;

    // Use HEAD to check accessibility without downloading the body.
    // GET without consuming the body causes Chrome to write a partial entry
    // to its HTTP cache, which makes subsequent new Audio(url) playback stop
    // at the same byte position every time that partial entry is served.
    const response = await fetch(mediaUrl, {
      method: 'HEAD',
      headers: {
        'Accept': '*/*',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // For audio files, we can also verify the content type
    const contentType = response.headers.get('content-type');
    if (contentType && !contentType.startsWith('audio/') && !contentType.startsWith('image/') && !contentType.startsWith('video/')) {
      // If we get HTML, it might be a 404 page or proxy issue
      if (contentType.includes('text/html')) {
        logger.warn('Media file returned HTML instead of media content - possible proxy or file not found issue', {
          url: file.url,
          contentType,
          status: response.status,
        });
        // Still consider it "loaded" to avoid blocking, but log the warning
      } else {
        logger.warn('Unexpected content type for media file', {
          url: file.url,
          contentType,
        });
      }
    }

    logger.info('Media file preloaded successfully', {
      url: file.url,
      module: file.module,
      section: file.section,
      type: file.type,
      contentType,
    });

    return true;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logger.error('Failed to preload media file', {
      url: file.url,
      module: file.module,
      section: file.section,
      type: file.type,
      error: errorMessage,
    });
    return false;
  }
}

/**
 * Hook for preloading all simulation media files
 */
export function useSimulationMediaPreloader({
  level,
  examType,
  modules,
  settings,
  userId,
  enabled = true,
}: UseSimulationMediaPreloaderOptions): UseSimulationMediaPreloaderReturn {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [progress, setProgress] = useState<PreloadProgress>({
    totalFiles: 0,
    loadedFiles: 0,
    failedFiles: 0,
    currentModule: null,
    errors: [],
  });
  const [error, setError] = useState<Error | null>(null);
  const isCancelledRef = useRef(false);

  /**
   * Start preloading all media files
   */
  const startPreloading = useCallback(async () => {
    if (!settings || modules.length === 0 || !enabled) {
      setIsComplete(true);
      return;
    }

    setIsLoading(true);
    setIsComplete(false);
    setError(null);
    isCancelledRef.current = false;

    const allMediaFiles: MediaFile[] = [];
    const errors: Array<{ url: string; error: string }> = [];

    try {
      // Step 1: Fetch simulation data for all modules
      logger.info('Starting media preloading', {
        level,
        examType,
        modules: modules.length,
      });

      // Prefetch common simulation settings if not already in cache
      queryClient.prefetchQuery({
        queryKey: ['simulationSettings', level, examType],
        queryFn: () => findSimulationSettings(level, examType),
        staleTime: STALE_TIME_FIVE_MINUTES,
      });

      const moduleDataPromises = modules.map(async (module) => {
        if (isCancelledRef.current) return null;

        logger.info('Fetching simulation data', { module, level, examType });
        setProgress((prev) => ({ ...prev, currentModule: module }));

        try {
          // Prefetch module introduction metadata for TanStack Query cache
          // This ensures that when the user moves to the introduction screen,
          // the data is already in the cache and the transition is instantaneous.
          queryClient.prefetchQuery({
            queryKey: ['moduleIntroduction', module, examType, level],
            queryFn: () => fetchModuleIntroduction(module, examType, level),
            staleTime: STALE_TIME_FIVE_MINUTES,
          });

          let data: SimulationData | null = null;

          // Fetch module data and seed TanStack Query cache simultaneously
          // We use queryClient.fetchQuery/prefetchQuery pattern to ensure the data 
          // is seated in the cache with the exact same key used by useModuleData hook.
          const queryKey = [`${module}ModuleData`, examType, level];

          if (module === 'reading') {
            data = await queryClient.fetchQuery({
              queryKey,
              queryFn: () => fetchReadingModuleData(level, examType, settings.sectionSettings, userId),
              staleTime: STALE_TIME_FIVE_MINUTES,
            });
          } else if (module === 'listening') {
            data = await queryClient.fetchQuery({
              queryKey,
              queryFn: () => fetchListeningModuleData(level, examType, settings.sectionSettings, userId),
              staleTime: STALE_TIME_FIVE_MINUTES,
            });
          } else if (module === 'speaking') {
            data = await queryClient.fetchQuery({
              queryKey,
              queryFn: () => fetchSpeakingModuleData(level, examType, settings.sectionSettings, userId),
              staleTime: STALE_TIME_FIVE_MINUTES,
            });
          }
          // Add other modules as they're implemented

          if (data) {
            const mediaFiles = extractMediaUrls(data, module);
            allMediaFiles.push(...mediaFiles);
            logger.info('Extracted media URLs from module', {
              module,
              count: mediaFiles.length,
            });
          }

          // Also fetch introduction media if it exists
          const introduction = await fetchModuleIntroduction(module, examType, level);
          if (introduction?.material_url && introduction.material_type === 'audio') {
            allMediaFiles.push({
              url: introduction.material_url,
              module,
              type: 'introduction',
            });
          }

          return data;
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Unknown error';
          logger.error('Failed to fetch module data', { module, error: errorMessage });
          errors.push({ url: `module:${module}`, error: errorMessage });
          return null;
        }
      });

      await Promise.all(moduleDataPromises);

      if (isCancelledRef.current) {
        return;
      }

      // Deduplicate media files by URL
      const uniqueMediaFiles = Array.from(
        new Map(allMediaFiles.map((file) => [file.url, file])).values(),
      );

      logger.info('Total unique media files to preload', {
        count: uniqueMediaFiles.length,
        modules: modules.length,
      });

      // If no media files to preload, mark as complete immediately
      if (uniqueMediaFiles.length === 0) {
        logger.info('No media files to preload, skipping preload step');
        setIsLoading(false);
        setIsComplete(true);
        return;
      }

      setProgress((prev) => ({
        ...prev,
        totalFiles: uniqueMediaFiles.length,
        currentModule: null,
      }));

      // Step 2: Preload all media files
      let loadedCount = 0;
      let failedCount = 0;

      // Load files with limited concurrency (5 at a time)
      const CONCURRENT_LIMIT = 5;
      for (let i = 0; i < uniqueMediaFiles.length; i += CONCURRENT_LIMIT) {
        if (isCancelledRef.current) {
          break;
        }

        const batch = uniqueMediaFiles.slice(i, i + CONCURRENT_LIMIT);
        await Promise.all(
          batch.map(async (file) => {
            const success = await preloadMediaFile(file);
            if (success) {
              loadedCount++;
            } else {
              failedCount++;
              errors.push({
                url: file.url,
                error: 'Failed to load media file',
              });
            }
            setProgress((prev) => ({
              ...prev,
              loadedFiles: loadedCount,
              failedFiles: failedCount,
              errors: [...errors],
            }));
            return success;
          }),
        );
      }

      if (isCancelledRef.current) {
        return;
      }

      logger.info('Media preloading complete', {
        total: uniqueMediaFiles.length,
        loaded: loadedCount,
        failed: failedCount,
      });

      setIsLoading(false);
      setIsComplete(true);

      if (failedCount > 0) {
        logger.warn('Some media files failed to preload', {
          failed: failedCount,
          total: uniqueMediaFiles.length,
        });
        // Don't set error state for non-critical failures
        // Allow progression even if some files fail
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err : new Error('Unknown error');
      logger.error('Media preloading failed', errorMessage);
      setError(errorMessage);
      setIsLoading(false);
      setIsComplete(false);
    }
  }, [level, examType, modules, settings, userId, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isCancelledRef.current = true;
    };
  }, []);

  return {
    isLoading,
    isComplete,
    progress,
    error,
    startPreloading,
  };
}
